import { selectProgramType } from 'legacy/lut/features/partner360/configuration'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import {
  AbsolutePopup,
  IPopupMethods,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup'
import {
  DownloadPartnerPlanButton,
  ExportButton,
  usePartnerPlanExport,
  usePartnerPlanExportOptions,
  ValueTypeConfirmDialog,
} from 'partner360/features/partnerPlanExport'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { selectAvailableCockpitMetrics } from '../../../../model/selectors/selectAvailableCockpitMetrics'
import { selectCockpitDefaultExportPDFMetric } from '../../../../model/selectors/selectCockpitDefaultExportPDFMetric'
import { selectEffectiveCockpitFYear } from '../../../../model/selectors/selectEffectiveCockpitFYear'
import { CockpitSegmentedControlAccent } from '../../../components/cockpitSegmentedControlAccent'

import styles from './CockpitPlanExport.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks

interface IProps {
  partnerId: string
  partnerName?: string
  expanded?: boolean
}

export const CockpitPlanExport = ({ partnerId, partnerName, expanded = false }: IProps) => {
  const plan = usePartnerPlanExportOptions()
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const programType = useSelector(selectProgramType)
  const actualsValueTypeDefault = useSelector(selectCockpitDefaultExportPDFMetric)
  const availableExportMetrics = useSelector(selectAvailableCockpitMetrics)
  const {
    actualsValueType,
    actualsValueTypes,
    actualsValueTypeForConfirm,
    netNdpChangeHandler,
    confirmActualsValueTypeHandler,
    closeConfirmActualsValueTypeDialogHandler,
  } = usePartnerPlanExport({
    actualsValueTypes: availableExportMetrics,
    actualsValueTypeDefault,
    partnerId,
    fYear,
  })
  const [hasSaveError, setHasSaveError] = useState(false)
  const [open, setOpen] = useState(false)
  const [formatMenuOpen, setFormatMenuOpen] = useState(false)
  const popupRef = useRef<IPopupMethods>(null)
  const formatPopupRef = useRef<IPopupMethods>(null)
  const keepPopupOpenForConfirmation = useRef(false)
  keepPopupOpenForConfirmation.current =
    open && actualsValueTypeForConfirm !== ActualsValueTypes.None
  if (plan.isPartnerUser) return null

  const exportTitle = <Translation text={KEYS.exportPlan} />
  const panel = (
    <div className={styles.panel}>
      <div>
        <strong>{exportTitle}</strong>
        <div className={styles.context}>
          <Translation
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks.exportContext}
            replaceParams={[
              { key: '{partner}', value: partnerName || partnerId },
              { key: '{year}', value: String(fYear).slice(-2) },
            ]}
          />
        </div>
      </div>
      <CockpitSegmentedControlAccent>
        <SegmentedControl
          useItemKeys
          selectedItem={actualsValueType}
          onItemSelect={async (metric: ActualsValueTypes) => {
            setHasSaveError(false)
            try {
              await netNdpChangeHandler(metric)
            } catch {
              setHasSaveError(true)
            }
          }}
          size="small"
          variant="veneer"
        >
          {actualsValueTypes.map((metric) => (
            <SegmentedControl.Item key={metric} itemKey={metric}>
              <span>{metric}</span>
            </SegmentedControl.Item>
          ))}
        </SegmentedControl>
      </CockpitSegmentedControlAccent>
      {hasSaveError && (
        <div>
          <Translation text={KEYS.exportMetricSaveError} />
        </div>
      )}
      <AbsolutePopup
        ref={formatPopupRef}
        rootClassName={styles.formatTrigger}
        popupClassName={styles.popupWrap}
        visibilityCallback={(visible) => {
          setFormatMenuOpen(visible)
          return visible
        }}
        verticalStartPosition={TPopupVertical.Top}
        verticalDirection={TPopupVertical.Top}
        popupContent={({ action }) => (
          <PopPaper shadowPosition="auto" className={styles.formats}>
            {plan.exportOptions.map((option) => (
              <ExportButton
                key={`${option.extension}${option.pdfVersion ?? ''}`}
                partnerId={partnerId}
                fYear={fYear}
                programType={programType}
                extension={option.extension}
                pdfVersion={option.pdfVersion}
                buttonTranslation={
                  option.pdfVersion === 2
                    ? KEYS.exportFormatPdfV2
                    : KEYS.exportFormat[option.extension]
                }
                loadingTranslation={KEYS.exportLoading}
                errorTranslations={{
                  title: KEYS.exportErrorTitle,
                  text: KEYS.exportErrorText,
                  close: KEYS.exportErrorClose,
                }}
                shouldGeneratePDFOnBackend={option.shouldGeneratePDFOnBackend}
                buttonVariant="text"
                buttonSize="md"
                onExport={action.close}
                metric={actualsValueType}
              />
            ))}
          </PopPaper>
        )}
      >
        <div
          onMouseDown={(event) => {
            if (!formatMenuOpen) return
            event.stopPropagation()
            event.preventDefault()
            formatPopupRef.current?.close()
          }}
        >
          <Button className={styles.action} icon="kbase kbase-chevron-bottom" iconPosition="end">
            <Translation text={KEYS.exportAs} />
          </Button>
        </div>
      </AbsolutePopup>
      {plan.isShowDownloadPlan && (
        <DownloadPartnerPlanButton
          programType={programType}
          buttonTranslation={KEYS.downloadPlan}
          buttonVariant="outlined"
          className={styles.action}
        />
      )}
    </div>
  )

  return (
    <>
      {expanded ? (
        panel
      ) : (
        <AbsolutePopup
          ref={popupRef}
          rootClassName={styles.compactTrigger}
          popupClassName={styles.popupWrap}
          verticalStartPosition={TPopupVertical.Top}
          verticalDirection={TPopupVertical.Top}
          horizontalStartPosition={TPopupHorizontal.Left}
          horizontalDirection={TPopupHorizontal.Right}
          visibilityCallback={(visible) => {
            if (!visible && keepPopupOpenForConfirmation.current) return true
            setOpen(visible)
            return visible
          }}
          popupContent={() => (
            <PopPaper shadowPosition="auto" className={styles.paper}>
              {panel}
            </PopPaper>
          )}
        >
          <div
            onMouseDown={(event) => {
              if (!open) return
              event.stopPropagation()
              event.preventDefault()
              popupRef.current?.close()
            }}
          >
            <Button variant="text" className={styles.trigger}>
              <span className={styles.triggerContent}>
                <span className={styles.triggerIcon}>
                  <i className="kbase kbase-download" />
                </span>
                <span className={styles.triggerText}>{exportTitle}</span>
                <i className={`kbase kbase-chevron-${open ? 'top' : 'bottom'}`} />
              </span>
            </Button>
          </div>
        </AbsolutePopup>
      )}
      <ValueTypeConfirmDialog
        translationKey={KEYS.exportMetricConfirmation}
        errorTranslationKey={KEYS.exportMetricSaveError}
        actualsValueType={actualsValueTypeForConfirm}
        show={actualsValueTypeForConfirm !== ActualsValueTypes.None}
        onConfirm={confirmActualsValueTypeHandler}
        onClose={closeConfirmActualsValueTypeDialogHandler}
      />
    </>
  )
}
